/**
 * WindowResizeListener
 * React component for listening to window resize events
 */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

class WindowResizeListener extends React.Component {
  static displayName = 'WindowResizeListener';

  static propTypes = {
    /**
     * Called at least once soon after being mounted
     * type WindowSize = { windowWidth: number, windowHeight: number }
     * type onResize = (windowSize: WindowSize) => void
     */
    onResize: PropTypes.func.isRequired,
  };

  /**
   * List of resize listeners
   * @private
   */
  static _listeners = [];

  /**
   * Maximum debounce wait time
   * @public
   */
  static DEBOUNCE_TIME = 100;

  /**
   * Resize handler
   * Gets the window size and calls each listener
   * @private
   */
  static _onResize() {
    const windowWidth = window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    const windowHeight = window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    WindowResizeListener._listeners.forEach((listener) => {
      listener({
        windowWidth,
        windowHeight,
      });
    });
  }

  componentDidMount() {
    // Defer creating _debouncedResize until it's mounted
    // This allows users to change DEBOUNCE_TIME if they want
    // If there's no listeners, we need to attach the window listener
    if (!WindowResizeListener._listeners.length) {
      WindowResizeListener._debouncedResize = _.debounce(
        WindowResizeListener._onResize,
        WindowResizeListener.DEBOUNCE_TIME,
      );
      window.addEventListener('resize', WindowResizeListener._debouncedResize, false);
    }
    WindowResizeListener._listeners.push(this.props.onResize);
    WindowResizeListener._debouncedResize();
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.onResize !== this.props.onResize;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.onResize !== this.props.onResize) {
      const idx = WindowResizeListener._listeners.indexOf(prevProps.onResize);
      WindowResizeListener._listeners.splice(idx, 1, this.props.onResize);
    }
  }

  componentWillUnmount() {
    const idx = WindowResizeListener._listeners.indexOf(this.props.onResize);
    WindowResizeListener._listeners.splice(idx, 1);
    if (!WindowResizeListener._listeners.length) {
      window.removeEventListener('resize', WindowResizeListener._debouncedResize, false);
    }
  }

  render() {
    return <span />;
  }
}

export default WindowResizeListener;



// WEBPACK FOOTER //
// ./src/common/components/WindowResizeListener.js